import React, { useState } from "react";
import styled from "styled-components";
import ModalAlarm from "../../../../components/modal/NewModalAlarm";
import utilsService from "../../../../../servises/utils.service";
import {
    EmptyWidgetStyled
} from "../../../../../style/components/common.styled";
import AlarmMoreInfo from "../../../../components/alarmsMoreInfo/AlarmMoreInfo";
import alarmsStore from "../../../../../stores/alarms.store";
import { EuiIcon, EuiToolTip } from "@elastic/eui";
import { addToast } from '../../../../components/toasts/Toast';

const StylesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const HeaderStyled = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.85);
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const MarkAllAsRead = styled.button`
  background: none;
  border: none;
  color: #66d9ef;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  padding: 0;
  transition: color 0.15s ease;

  &:hover {
    color: #a3ebff;
    text-decoration: underline;
  }
`;

const ListContainerStyled = styled.div`
  height: 280px;
  overflow-y: auto;
  padding-right: 4px;

  /* Custom modern scrollbar styles */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.25);
    }
  }

  @media (max-width: 600px) {
    height: auto;
    overflow-y: visible;
  }
`;

const ItemGroupStyles = styled.div`
  margin-bottom: 18px;

  &:last-child {
    margin-bottom: 0;
  }

  .group-label {
    color: rgba(255, 255, 255, 0.45);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 8px 2px;
    display: block;
  }
`;

const AlarmItemStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(72, 77, 91, 0.4);
  border-left: 4px solid ${props => props.severityColor};
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.055);
    border-color: rgba(72, 77, 91, 0.7);
    transform: translateX(3px);
  }

  &.read {
    opacity: 0.65;
    border-left-color: rgba(255, 255, 255, 0.2) !important;
  }
`;

const AlarmHeaderStyled = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const ServerBadgeStyled = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.06);
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const AlarmTimeStyled = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.42);
  font-family: monospace;
  white-space: nowrap;
`;

const AlarmTextStyled = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.4;
  word-break: break-word;
`;

const getSeverityColor = (severityClass) => {
    switch (severityClass) {
        case "critical":
            return "#F86B63";
        case "warning":
            return "#F9BE49";
        case "info":
        default:
            return "#36A2EF";
    }
};

const AlarmsWidget = ({ alarms, server, hideTitle }) => {
    const [showModal, setShowModal] = useState(false);
    const [alarmForModal, setAlarmForModal] = useState(null);

    const showAlarm = (alarm, text, server, severity) => {
        setAlarmForModal({ alarm, text, server, severity });
        setShowModal(true);
    };

    const checkIfAlarms = () => {
        let verdict = false;

        if (alarms) {
            if (alarms.today && alarms.today.length > 0) {
                verdict = true;
            }
            if (alarms.rest && alarms.rest.length > 0) {
                verdict = true;
            }
            if (alarms.lastWeek && alarms.lastWeek.length > 0) {
                verdict = true;
            }
        }

        return verdict;
    };

    const haveAlarms = checkIfAlarms();

    const markAllAlarmsAsReadHandle = async () => {
        const ids = [];

        if (alarms.today && alarms.today.length > 0) {
            alarms.today.forEach(al => {
                ids.push(al._id);
            });
        }
        if (alarms.rest && alarms.rest.length > 0) {
            alarms.rest.forEach(al => {
                ids.push(al._id);
            });
        }
        if (alarms.lastWeek && alarms.lastWeek.length > 0) {
            alarms.lastWeek.forEach(al => {
                ids.push(al._id);
            });
        }

        try {
            await alarmsStore.setAllAlarmStatusRead(ids);
        } catch (error) {
            addToast(error.message || 'Failed to mark alarms as read', 'error');
        }
    };

    return (
        <StylesWrapper>
            <HeaderStyled>
                { !hideTitle && (
                    <h3><EuiIcon type="alert" size="m"/> Active Alarms</h3>
                ) }

                { haveAlarms && (
                    <MarkAllAsRead
                        type="button"
                        onClick={ markAllAlarmsAsReadHandle }
                    >
                        Mark all as read
                    </MarkAllAsRead>
                ) }
            </HeaderStyled>
            <ListContainerStyled>
                { haveAlarms && (
                    <>{ haveAlarms && alarms.today.length > 0 &&
                        (<ItemGroupStyles>
                            <span className="group-label">Last 24 hours</span>
                            { haveAlarms && alarms.today.map(alarm => {
                                return (
                                    <AlarmItemO
                                        key={ alarm._id }
                                        data={ alarm }
                                        showAlarm={ showAlarm }
                                        server={ server }
                                    />
                                );
                            }) }
                        </ItemGroupStyles>)
                    }

                        { haveAlarms && alarms.lastWeek.length > 0 && (
                            <ItemGroupStyles>
                                <span className="group-label">Last Week</span>
                                { haveAlarms && alarms.lastWeek.map(alarm => {
                                    return (
                                        <AlarmItemO
                                            key={ alarm._id }
                                            data={ alarm }
                                            showAlarm={ showAlarm }
                                            server={ server }
                                        />
                                    );
                                }) }
                            </ItemGroupStyles>
                        ) }

                        { haveAlarms && alarms.rest.length > 0 && (
                            <ItemGroupStyles>
                                <span className="group-label">Old Alarms</span>
                                { haveAlarms && alarms.rest.map(alarm => {
                                    return (
                                        <AlarmItemO
                                            key={ alarm._id }
                                            data={ alarm }
                                            showAlarm={ showAlarm }
                                            server={ server }
                                        />
                                    );
                                }) }
                            </ItemGroupStyles>
                        ) }
                    </>
                ) }

                { !haveAlarms && (
                    <EmptyWidgetStyled>
                        Yay, no alarms
                    </EmptyWidgetStyled>
                ) }

                { showModal && (
                    <ModalAlarm
                        data={ alarmForModal }
                        close={ () => setShowModal(false) }
                    />
                ) }
            </ListContainerStyled>
        </StylesWrapper>
    );
};

export default AlarmsWidget;

const AlarmItemO = ({ data, showAlarm, server }) => {
    const severityColor = getSeverityColor(data.severityData.classes);
    return (
        <AlarmItemStyled
            className={ data.statusClass }
            key={ data._id }
            severityColor={ severityColor }
            onClick={ () => showAlarm(data, data.text, server,
                data.severityData
            ) }
        >
            <AlarmHeaderStyled>
                <ServerBadgeStyled>{ data.serverName }</ServerBadgeStyled>
                <AlarmTimeStyled>
                    { utilsService.timeMe(data.createdAt) } &middot; { utilsService.dateMe(data.createdAt) }
                </AlarmTimeStyled>
            </AlarmHeaderStyled>

            <EuiToolTip
                position="left"
                content={
                    <AlarmTooltip
                        alarm={ data }
                        severity={ data.severityData }
                        text={ data.text }
                    />
                }
            >
                <AlarmTextStyled>{ data.text }</AlarmTextStyled>
            </EuiToolTip>
        </AlarmItemStyled>
    );
};

const AlarmTooltip = ({ alarm, severity, text }) => {
    return (
        <AlarmMoreInfo
            alarm={ alarm }
            severity={ severity }
            text={ text }
            compact
        />
    );
};
